// Hard-Gate §22 (2026-09-06) — self-service tenant data export.
// "DOWNLOAD MY COMPANY DATA" — tenant owner/admin only (MFA-gated, same
// as any other privileged action — see backend/routers/tenant_export.py).
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Download, Loader2, FileArchive, AlertTriangle, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  queued: "Queued",
  running: "Preparing your export…",
  completed: "Ready to download",
  failed: "Failed",
};

export function DataExportCard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const pollRef = useRef(null);

  const isAdmin = user && ["admin", "owner"].includes(user.role);

  const loadJobs = async () => {
    try {
      const { data } = await api.get("/tenant/data-exports");
      setJobs(data);
      return data;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load export history");
      return null;
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const active = jobs?.find((j) => j.status === "queued" || j.status === "running");
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const fresh = await loadJobs();
        const stillActive = fresh?.find((j) => j.status === "queued" || j.status === "running");
        if (!stillActive) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 3000);
    }
    return () => {
      if (pollRef.current && !active) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobs]);

  if (!isAdmin) return null;

  const requestExport = async () => {
    setRequesting(true);
    setError("");
    try {
      await api.post("/tenant/data-exports");
      toast.success("Export started — this page will update automatically.");
      await loadJobs();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start export");
    } finally {
      setRequesting(false);
    }
  };

  const download = async (jobId) => {
    try {
      const { data } = await api.get(`/tenant/data-exports/${jobId}/download`, {
        responseType: "blob",
        validateStatus: () => true,
        transformResponse: [(d) => d],
      });
      // If backend returned a signed URL (R2), it's JSON, not a blob.
      const text = typeof data === "string" ? data : null;
      if (text && text.startsWith("{")) {
        const parsed = JSON.parse(text);
        if (parsed.download_url) {
          window.location.href = parsed.download_url;
          return;
        }
      }
      const blob = new Blob([data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "company_data_export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed — export link may have expired.");
    }
  };

  const active = jobs?.find((j) => j.status === "queued" || j.status === "running");

  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="data-export-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
          Company Data Export
        </div>
        <FileArchive size={14} className="text-[var(--fop-text-3)]" />
      </div>
      <p className="text-sm text-[var(--fop-text-2)] mb-4">
        Download every project, sheet, document, photo, measurement, and user record your company owns —
        a single portable archive, ready any time.
      </p>

      {error && (
        <div className="flex items-start gap-2 text-xs text-[var(--st-escalated)] mb-3" data-testid="data-export-error">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <button
        onClick={requestExport}
        disabled={requesting || !!active}
        className="btn btn-primary text-sm"
        data-testid="data-export-request-button"
      >
        {requesting || active ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        {active ? STATUS_LABEL[active.status] : requesting ? "Starting…" : "Download my company data"}
      </button>

      {jobs && jobs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="data-export-history">
          {jobs.slice(0, 5).map((j) => (
            <div
              key={j.id}
              className="flex items-center justify-between text-xs border border-[var(--fop-border)] rounded px-3 py-2"
              data-testid={`data-export-row-${j.id}`}
            >
              <div className="flex items-center gap-2 text-[var(--fop-text-2)]">
                {(j.status === "queued" || j.status === "running") && (
                  <RefreshCw size={11} className="animate-spin text-[var(--fop-primary)]" />
                )}
                <span className="font-mono">{new Date(j.created_at).toLocaleString()}</span>
                <span className="text-[var(--fop-text-3)]">— {STATUS_LABEL[j.status] || j.status}</span>
                {j.status === "completed" && j.size_bytes ? (
                  <span className="text-[var(--fop-text-4)]">({(j.size_bytes / 1024).toFixed(0)} KB)</span>
                ) : null}
              </div>
              {j.status === "completed" && (
                <button
                  onClick={() => download(j.id)}
                  className="btn btn-ghost text-[11px]"
                  data-testid={`data-export-download-${j.id}`}
                >
                  <Download size={11} /> Download
                </button>
              )}
              {j.status === "failed" && j.error && (
                <span className="text-[var(--st-escalated)]" data-testid={`data-export-error-${j.id}`}>{j.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
